import { useUnitStockingDashboard, getWidgets, UNIT_STOCKING_DASHBOARD} from './useUnitStockingDashboard';
import {DashboardWidget} from './DashboardWidget';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { annotateWidgetsWithPermission } from './permissionUtil';
import useUserSettingStore from './UserSettingStore';
import userProfileStore from '../core/UserProfileStore';

const styles= {
  flexWrapper: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '16px',
    alignItems: 'stretch',
  },
  standardWidget: { 
    flex: '1 1 300px', // Standard chart size
    display: 'flex',
    gap:'20px',
    flexDirection: 'column' as const,
  },
  fillWidget: {
    flex: '1', 
    display: 'flex',
    flexDirection: 'column'
  },
  emptyState: {
    padding: '24px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#4b5563',
    fontSize: '14px',
  },
};

export const useDashboardRegistry = () => {
  const { t } = useTranslation();
  const { dashboardAccessSetting } = useUserSettingStore();
  const { permissions } = userProfileStore();
  
  const usPermissionWidgets = useMemo(() => {
    return annotateWidgetsWithPermission(
      getWidgets(t),
      UNIT_STOCKING_DASHBOARD.DOMAIN_ID,
      dashboardAccessSetting,
      permissions
    );
  }, [dashboardAccessSetting, permissions, t]);

  const usDashboard = useUnitStockingDashboard({
    enabled: Array.isArray(usPermissionWidgets) && usPermissionWidgets.some((w) => w.hasPermission),
    filteredWidgets: usPermissionWidgets,
  });

  const dashboards = [
    { id: UNIT_STOCKING_DASHBOARD.DASHBOARD_ID, ...usDashboard },
  ] as any;

  return {
    usDashboard,
    getWidgetState: (dashboardId:string, widgetId:string) => {
      const dashboard = dashboards.find((d:any) => d.id === dashboardId);
      const widget = dashboard.widgets?.find((w:any) => w.id === widgetId);
      return {
        isLoading: dashboard.isLoading,
        error: dashboard.error,
        widgetData: widget ? dashboard.getWidgetData(widget) : null,
        widget: widget
      };
    }
  };
  
};

export const USDashboard = () => {
  const { getWidgetState, usDashboard } = useDashboardRegistry();
  const { t } = useTranslation();
  
  const usWidgets = usDashboard?.widgets || [];
  const shouldShowEmptyState = !usDashboard?.isLoading && usWidgets.length === 0;

  const renderDashboardWidget = (widgetId: string, loaderCount?: number) => {
    const dashboardWidget = usWidgets.find((w: any) => w.id === widgetId);
    if (!dashboardWidget) {
      return null;
    }

    return (
      <DashboardWidget
        state={getWidgetState(UNIT_STOCKING_DASHBOARD.DASHBOARD_ID, widgetId)}
        loaderCount={loaderCount}
      />
    );
  };

  const stockMissingWidget = renderDashboardWidget(UNIT_STOCKING_DASHBOARD.WIDGET_STOCK_MISSING_ID, 19);
  const stockAgingWidget = renderDashboardWidget(UNIT_STOCKING_DASHBOARD.WIDGET_STOCK_AGING_ID);
  const stockInvMarkWidget = renderDashboardWidget(UNIT_STOCKING_DASHBOARD.WIDGET_STOCK_INV_MARK_ID);
  const stockInvStatusWidget = renderDashboardWidget(UNIT_STOCKING_DASHBOARD.WIDGET_STOCK_INV_STATUS_ID);
  const stockInvCategoryWidget = renderDashboardWidget(UNIT_STOCKING_DASHBOARD.WIDGET_STOCK_INV_CAT_ID);

  return (
       <div >
        {shouldShowEmptyState && (
          <div style={styles.emptyState}>
            {t('dashboard.insufficientPermissions', 
              'This chart is unavailable as you do not have required permissions.')}
          </div>
        )}
        <div style={styles.flexWrapper}>
          {stockMissingWidget && 
            <div style={{ ...styles.standardWidget }}>
              {stockMissingWidget}
            </div>
          }
        <div style={{ ...styles.standardWidget }}>
          {stockAgingWidget}
          {stockInvMarkWidget}
        </div>
        {stockInvStatusWidget && 
          <div style={{ ...styles.standardWidget }}>
            {stockInvStatusWidget}
          </div>
      }
        {stockInvCategoryWidget && 
         <div style={{ ...styles.standardWidget }}>
          {stockInvCategoryWidget}
        </div>
      }
        </div>
    </div>
  );
};
